// src/utils/economicReport.ts
//
// Builds the downloadable "Economic Report" from an AdminSalesSummary payload —
// a P&L-style summary plus a full per-product revenue / cost / margin breakdown.
// Two formats: a self-contained, printable HTML document (opens in any browser,
// print → Save as PDF) and a raw CSV for spreadsheet analysis.
//
// Pure string builders (no DOM) so the numbers are easy to reason about; the
// only browser bit is triggerDownload(). The margin caveats mirror the dashboard
// UI and docs/admin-sales-dashboard.md — see that doc for the honest-not-
// accounting-grade revenue definitions this inherits.

import { AdminSalesSummary } from "../api/admin";

/** cents → "$1,234.56". */
const money = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** cents → "1234.56" (plain decimal dollars for CSV cells). */
const dollars = (cents: number): string => (cents / 100).toFixed(2);

/** Human label for the selected window (0 days = all time). */
export const rangeLabelFor = (days: number): string => {
  if (!days) return "All time";
  if (days === 7) return "Last 7 days";
  if (days === 30) return "Last 30 days";
  if (days === 90) return "Last 90 days";
  if (days === 365) return "Last year";
  return `Last ${days} days`;
};

const escapeHtml = (s: string): string =>
  s.replace(
    /[&<>"']/g,
    (c) =>
      (
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }) as Record<string, string>
      )[c],
  );

/** RFC-4180 CSV cell: quote when it contains a comma, quote, or newline. */
const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const timestamp = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

// ── HTML report ─────────────────────────────────────────────────────────────

/** A self-contained, printable HTML document for the given summary. */
export const buildReportHtml = (data: AdminSalesSummary): string => {
  const hasMargin = data.costSnapshot.available && data.costedUnits > 0;
  const range = rangeLabelFor(data.days);
  const snap = data.costSnapshot;

  // Waterfall: Collected → −tax → −shipping = product revenue → −COGS = margin.
  const line = (
    label: string,
    value: number,
    opts: { strong?: boolean; sign?: "minus" | "eq"; accent?: string } = {},
  ) => {
    const prefix =
      opts.sign === "minus" ? "− " : opts.sign === "eq" ? "= " : "";
    return `<tr class="${opts.strong ? "strong" : ""}">
      <td>${prefix}${escapeHtml(label)}</td>
      <td class="num ${opts.accent ?? ""}">${money(value)}</td>
    </tr>`;
  };

  const productRows = data.topProducts
    .map((p) => {
      const marginCell =
        p.costed && p.marginCents != null
          ? `<span class="${p.marginCents >= 0 ? "pos" : "neg"}">${money(
              p.marginCents,
            )}${p.marginPct != null ? ` <span class="muted">(${p.marginPct}%)</span>` : ""}</span>`
          : `<span class="muted" title="No cost on file">—</span>`;
      const cogsCell = p.costed
        ? money(p.cogsCents)
        : `<span class="muted">—</span>`;
      return `<tr>
        <td>${escapeHtml(p.name)}</td>
        <td class="num">${p.quantity}</td>
        <td class="num">${money(p.revenueCents)}</td>
        <td class="num">${cogsCell}</td>
        <td class="num">${marginCell}</td>
      </tr>`;
    })
    .join("");

  const caveats: string[] = [
    "Revenue counts money actually collected — orders with a captured payment that isn't fully refunded. Abandoned, authorized-but-not-captured, voided, and fully-refunded orders are excluded.",
    "Partial refunds are not deducted from revenue.",
    "COGS uses cached Printful blank/base costs — this covers one print but NOT embroidery stitching or extra placements, so embroidered / multi-placement items read optimistically (margin overstated).",
  ];
  if (data.uncostedUnits > 0)
    caveats.push(
      `${data.uncostedUnits} sold unit(s) had no cost on file and are excluded from margin (shown as “—”).`,
    );
  if (data.refundedCents > 0)
    caveats.push(
      `${money(data.refundedCents)} in partial refunds on counted orders is not reflected in the totals above.`,
    );
  if (data.truncated)
    caveats.push(
      "The order scan hit its page cap — the oldest orders in this window are not included. Narrow the range for a complete picture.",
    );

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Economic Report — Americana &amp; Company (${escapeHtml(range)})</title>
<style>
  :root { --ink:#2b2320; --muted:#8a7f77; --line:#e6ddd3; --cream:#faf6f0;
          --pos:#15803d; --neg:#b91c1c; --accent:#7c2d12; }
  * { box-sizing: border-box; }
  html { background: #fff; }
  body { font-family: Georgia, "Times New Roman", serif; color: var(--ink);
         background: #fff; max-width: 820px; margin: 0 auto; padding: 32px 24px;
         line-height: 1.5; }
  header { border-bottom: 3px double var(--accent); padding-bottom: 16px; margin-bottom: 24px; }
  h1 { font-size: 24px; margin: 0 0 4px; color: var(--accent); letter-spacing: .5px; }
  .sub { color: var(--muted); font-size: 14px; }
  .badge { display:inline-block; font-size:11px; text-transform:uppercase; letter-spacing:.5px;
           border:1px solid var(--line); border-radius:999px; padding:2px 10px; margin-left:8px;
           vertical-align:middle; color:var(--muted); }
  h2 { font-size: 15px; text-transform: uppercase; letter-spacing: 1px; color: var(--accent);
       border-bottom: 1px solid var(--line); padding-bottom: 6px; margin: 28px 0 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td, th { padding: 7px 10px; }
  .pl td { border-bottom: 1px solid var(--line); }
  .pl tr.strong td { font-weight: bold; border-top: 1px solid var(--ink); }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  thead th { text-align: left; background: var(--cream); border-bottom: 1px solid var(--line);
             font-size: 12px; text-transform: uppercase; letter-spacing: .5px; color: var(--muted); }
  thead th.num { text-align: right; }
  tbody tr { border-bottom: 1px solid var(--line); }
  .pos { color: var(--pos); } .neg { color: var(--neg); } .muted { color: var(--muted); }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 4px 0 8px; }
  .stat { border: 1px solid var(--line); border-radius: 8px; padding: 10px; text-align: center; }
  .stat .v { font-size: 18px; font-weight: bold; }
  .stat .l { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; }
  footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid var(--line);
           font-size: 12px; color: var(--muted); }
  footer li { margin-bottom: 5px; }
  @media print { body { margin: 0; max-width: none; } .noprint { display: none; } }
</style>
</head>
<body>
<header>
  <h1>Economic Report<span class="badge">${escapeHtml(data.environment)}</span></h1>
  <div class="sub">Americana &amp; Company · ${escapeHtml(range)} · generated ${escapeHtml(
    timestamp(data.generatedAt),
  )}</div>
</header>

<div class="grid">
  <div class="stat"><div class="v">${data.orderCount}</div><div class="l">Orders</div></div>
  <div class="stat"><div class="v">${money(data.aovCents)}</div><div class="l">Avg order value</div></div>
  <div class="stat"><div class="v">${money(data.productRevenueCents)}</div><div class="l">Product revenue</div></div>
  <div class="stat"><div class="v ${
    hasMargin ? (data.marginCents >= 0 ? "pos" : "neg") : "muted"
  }">${hasMargin ? money(data.marginCents) : "—"}</div><div class="l">Gross margin</div></div>
</div>

<h2>Profit &amp; Loss</h2>
<table class="pl">
  ${line("Collected (incl. tax & shipping)", data.totalCollectedCents)}
  ${line("Sales tax", data.taxCents, { sign: "minus" })}
  ${line("Shipping", data.shippingCents, { sign: "minus" })}
  ${line("Product revenue", data.productRevenueCents, { sign: "eq", strong: true })}
  ${line("Cost of goods sold (Printful)", data.cogsCents, { sign: "minus" })}
  ${line(
    hasMargin
      ? `Gross margin${data.marginPct != null ? ` (${data.marginPct}%)` : ""}`
      : "Gross margin (unavailable — refresh costs)",
    hasMargin ? data.marginCents : 0,
    {
      sign: "eq",
      strong: true,
      accent: hasMargin && data.marginCents >= 0 ? "pos" : "neg",
    },
  )}
</table>
<p class="sub" style="margin-top:8px">
  Discounts applied: ${money(data.discountCents)} ·
  Cost data: ${
    snap.available
      ? `${snap.costedVariants ?? 0} variants costed${
          snap.uncostedVariants ? `, ${snap.uncostedVariants} uncosted` : ""
        }${snap.updatedAt ? ` (updated ${escapeHtml(timestamp(snap.updatedAt))})` : ""}`
      : "not refreshed — margin unavailable"
  }
</p>

<h2>Per-product breakdown</h2>
<table>
  <thead>
    <tr>
      <th>Product</th>
      <th class="num">Qty</th>
      <th class="num">Revenue</th>
      <th class="num">COGS</th>
      <th class="num">Margin</th>
    </tr>
  </thead>
  <tbody>
    ${productRows || `<tr><td colspan="5" class="muted">No products sold in this window.</td></tr>`}
  </tbody>
</table>

<footer>
  <strong>Notes</strong>
  <ul>${caveats.map((c) => `<li>${escapeHtml(c)}</li>`).join("")}</ul>
</footer>
</body>
</html>`;
};

// ── CSV report ──────────────────────────────────────────────────────────────

/** Raw CSV: a summary block then one row per product. Dollars, 2 decimals. */
export const buildReportCsv = (data: AdminSalesSummary): string => {
  const rows: string[] = [];
  rows.push(
    ["section", "label", "qty", "revenue", "cogs", "margin", "marginPct"].join(
      ",",
    ),
  );

  const sum = (
    label: string,
    revenue = "",
    cogs = "",
    margin = "",
    marginPct = "",
  ) =>
    rows.push(
      ["summary", label, "", revenue, cogs, margin, marginPct]
        .map(csvCell)
        .join(","),
    );

  sum("Orders", "", "", "", "");
  rows[rows.length - 1] = ["summary", "Orders", data.orderCount, "", "", "", ""]
    .map(csvCell)
    .join(",");
  sum("Collected (incl tax/ship)", dollars(data.totalCollectedCents));
  sum("Sales tax", dollars(data.taxCents));
  sum("Shipping", dollars(data.shippingCents));
  sum("Product revenue", dollars(data.productRevenueCents));
  sum("Discounts", dollars(data.discountCents));
  sum("COGS", "", dollars(data.cogsCents));
  sum(
    "Gross margin",
    "",
    "",
    dollars(data.marginCents),
    data.marginPct != null ? String(data.marginPct) : "",
  );
  sum("Avg order value", dollars(data.aovCents));
  if (data.refundedCents > 0)
    sum("Partial refunds (not deducted)", dollars(data.refundedCents));

  for (const p of data.topProducts) {
    rows.push(
      [
        "product",
        p.name,
        p.quantity,
        dollars(p.revenueCents),
        p.costed ? dollars(p.cogsCents) : "",
        p.costed && p.marginCents != null ? dollars(p.marginCents) : "",
        p.costed && p.marginPct != null ? String(p.marginPct) : "",
      ]
        .map(csvCell)
        .join(","),
    );
  }

  return rows.join("\r\n");
};

// ── Download trigger ────────────────────────────────────────────────────────

/** Turn a range + timestamp into a filesystem-friendly slug. */
const fileSlug = (data: AdminSalesSummary): string =>
  `economic-report_${rangeLabelFor(data.days)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}_${data.generatedAt.slice(0, 10)}`;

const triggerDownload = (
  filename: string,
  content: string,
  mime: string,
): void => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadReportHtml = (data: AdminSalesSummary): void =>
  triggerDownload(
    `${fileSlug(data)}.html`,
    buildReportHtml(data),
    "text/html;charset=utf-8",
  );

export const downloadReportCsv = (data: AdminSalesSummary): void =>
  triggerDownload(
    `${fileSlug(data)}.csv`,
    buildReportCsv(data),
    "text/csv;charset=utf-8",
  );
